import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {
  MessageBusService,
  SearchFilterSortByOpts,
  SearchSourceListItem,
  StateNamePipe,
} from '@shared-library';
import {
  LighthouseBrandListDisplayItem,
  LighthouseSourceSearch,
  LighthouseSourceSearchAggregation
} from '@shared-library';
import {HttpClient} from '@angular/common/http';
import {debounceTime, distinctUntilChanged, mergeMap, Observable, of, Subject, Subscription, take} from 'rxjs';
import {SearchFilter} from '@shared-library';
import {FastenService} from '../../services/fasten.service';
import {ConfigService} from '@shared-library';
import {Router, RouterModule} from '@angular/router';
import {NGXLogger} from 'ngx-logger';
import {StateCodes} from "../../utils/state-codes";
import {CommonModule} from "@angular/common";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {InfiniteScrollModule} from "ngx-infinite-scroll";
import _ from "lodash";
import { FdkCardComponent, FdkIconComponent, FdkIconButtonComponent, FdkInputComponent, FdkSelectComponent, FdkSelectOption, FdkIllustrationComponent, FdkButtonComponent } from 'fasten-design-kit';
import {HealthSystemSearchSkeletonComponent} from './health-system-search-skeleton.component';

@Component({
    selector: 'app-health-system-search',
    templateUrl: './health-system-search.component.html',
    styleUrls: ['./health-system-search.component.css'],
    standalone: true,
    imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    FdkCardComponent,
    FdkIconComponent,
    FdkIconButtonComponent,
    FdkInputComponent,
    FdkSelectComponent,
    InfiniteScrollModule,
    FdkIllustrationComponent,
    FdkButtonComponent,
    HealthSystemSearchSkeletonComponent
]
})
export class HealthSystemSearchComponent implements OnInit, OnDestroy {
  loading = false;
  lighthouseBrandList: SearchSourceListItem[] = []
  private readonly stateNamePipe = new StateNamePipe();

  readonly stateOptions: readonly FdkSelectOption[] = [
    {label: 'All States', value: 'ALL'},
    {label: '-------', value: '', disabled: true},
    ...Object.entries(StateCodes).map(([label, value]) => ({label, value})),
  ];

  readonly sortOptions: readonly FdkSelectOption[] = [
    {label: 'Default', value: ''},
    {label: '-------', value: 'separator', disabled: true},
    {label: 'Current Location', value: 'location'},
  ];

  //these filter variables are bound to the form, however they must be stored in the config.searchConfig object when changes occur.
  showFilters: boolean = false
  filter: SearchFilter = new SearchFilter()
  requestingLocation = false;
  sortByLocationZipcode = ""

  //limits that are tied to the current result set.
  resultLimits: {
    totalItems: number,
    scrollComplete: boolean,
    platformTypesBuckets: LighthouseSourceSearchAggregation | undefined,
    categoryBuckets: LighthouseSourceSearchAggregation | undefined,
  } = {
    totalItems: 0,
    scrollComplete: false,
    platformTypesBuckets: undefined,
    categoryBuckets: undefined
  }

  private searchTermChanged = new Subject<string>();
  private searchTermSubscription: Subscription;

  constructor(
    public fastenService: FastenService,
    public configService: ConfigService,
    public router: Router,
    private logger: NGXLogger,
    private messageBus: MessageBusService,
  ) { }

  ngOnInit(): void {
    //initialize filter from config service, incase query, sort terms or filters were set previously.
    this.filter = _.merge(this.filter, this.configService.searchConfig$.searchFilter || {})
    if(this.filter?.sortByOpts?.locationZipcodes?.length){
      this.sortByLocationZipcode = this.filter.sortByOpts.locationZipcodes[0]
    }
    this.querySources(true)
    //
    this.searchTermSubscription = this.searchTermChanged.pipe(
      // filter out empty strings
      distinctUntilChanged(),
      // debounce to avoid too many requests
      debounceTime(500), // Adjust the debounce time as needed
      // mergeMap()
    ).subscribe(() => {
      this.logger.info("searchTermChanged", this.filter.query);
      this.querySources(true);
    });
  }
  ngOnDestroy() {
    if (this.searchTermSubscription) {
      this.searchTermSubscription.unsubscribe();
    }
  }

  onKeyup(event: Event) {
    this.searchTermChanged.next(this.filter.query);
  }

  public querySources(reset: boolean): Observable<LighthouseSourceSearch|null> {
    if(reset){
      this.resetSearch()
    }

    this.logger.debug("querySources()", this.filter)
    if(this.loading){
      this.logger.info("already loading, ignoring querySources()")
      return of(null)
    }
    //TODO: pass filter to function.
    // this.location.replaceState('/dashboard','', this.filter)

    if(!this.filter){
      this.filter = new SearchFilter()
      this.logger.info("querySources() - no filter provided, using current form value", this.filter)
    }
    this.filter.fields = ["*"];

    //copy the filter data to the searchConfig object as a form of caching. Only cache if this is a reset search.
    if(reset)
    {
      this.configService.searchConfig = {
        ...this.configService.searchConfig$,
        searchFilter: _.cloneDeep(this.filter)
      }
    }


    this.loading = true
    // this.filter.showHidden = true
    // this.filter.platformTypes = ["epic"]
    var searchObservable = this.fastenService.searchCatalog(this.configService.systemConfig$.apiMode!, this.filter);
    searchObservable.subscribe(wrapper => {
        this.logger.info("search sources", wrapper);
        // this.searchResults = wrapper.hits.hits;
        this.resultLimits.totalItems = wrapper?.hits?.total.value || 0;

        this.lighthouseBrandList = this.lighthouseBrandList.concat((wrapper?.hits?.hits || []).map((result) => {
          return {
            brand: result._source,
            searchHighlights: result?.highlight?.aliases || []
          }
        }))

        //check if scroll is complete.
        if(!wrapper?.hits || !wrapper?.hits?.hits || wrapper?.hits?.hits?.length == 0 || wrapper?.hits?.total?.value == wrapper?.hits?.hits?.length){
          this.logger.debug("SCROLL_COMPLETE!@@@@@@@@")
          this.resultLimits.scrollComplete = true;
        } else {
          //change the current Page (but don't cause a new query)
          this.logger.debug("SETTING NEXT SORT KEY:", wrapper.hits.hits[wrapper.hits.hits.length - 1].sort.join(','))
          this.filter.searchAfter = wrapper.hits.hits[wrapper.hits.hits.length - 1].sort.join(",")
        }
        this.loading = false

        // Emit search query event here (if it's the first time, ie. reset is true, and its not just a pagination)
        if(reset) {
          this.messageBus.publishSearchQuery(
            this.filter.query,
            this.filter.locations,
            this.configService.systemConfig$.externalId,
            wrapper?.hits?.total?.value
          );
        }
      },
      error => {
        this.loading = false
        this.logger.error("sources FAILED", error)
      },
      () => {
        this.loading = false
        this.logger.info("sources finished")
      }
    );
    return searchObservable;
  }

  onScroll() {
    if(!this.resultLimits.scrollComplete) {
      this.querySources(false)
    }
  }

  requestUserLocation(): void {
    if (this.requestingLocation) {
      return;
    }

    if (typeof navigator === 'undefined' || !navigator?.geolocation) {
      this.logger.warn('Geolocation is not supported in this environment.');
      return;
    }

    this.requestingLocation = true;
    this.logger.info('Requesting user geo location...');
    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.logger.info('User geo location provided.', position.coords);
        this.fastenService.reverseGeocodePostalCode(position.coords.latitude, position.coords.longitude)
          .subscribe({
            next: (response) => {


              this.logger.info("reverse geocode response:", response);
              if (response?.zip_code) {
                this.logger.info('Reverse geocoding resolved postal code', response.zip_code);
                this.filter.sortBy = 'location'
                this.sortByLocationZipcode = response.zip_code
                this.filter.sortByOpts.locationZipcodes = [response.zip_code];
                this.querySources(true)
              } else {
                this.logger.warn('No zipcode found for provided coordinates.', response);
              }
              this.requestingLocation = false;
            },
            error: (err) => {
              this.logger.error('Reverse geocoding failed', err);
              this.requestingLocation = false;
            }
          });
      },
      (error) => {
        this.logger.warn('Unable to retrieve geolocation.', error);
        this.requestingLocation = false;
      }
    );
  }

  private resetSearch(){
    this.logger.info("reset search...")
    this.lighthouseBrandList = []
    this.filter.searchAfter = []
    this.resultLimits = {
      totalItems: 0,
      scrollComplete: false,
      platformTypesBuckets: undefined,
      categoryBuckets: undefined
    }
  }

  public selectBrand(brandItem?: LighthouseBrandListDisplayItem){

    this.configService.searchConfig$.selectedBrand = brandItem
    this.router.navigateByUrl('brand/details')
  }

  getBrandLocations(locations: string[]): string[] {
    const displayLocations = locations.slice(0, 3).map(code => String(this.stateNamePipe.transform(code)));
    // Match the location summary shown on the brand-details page.
    if (locations.length > 4) {
      displayLocations.push(`+ ${locations.length}`);
    }
    return displayLocations;
  }

  getBrandInitials(name: string): string {
    const words = name.trim().split(/\s+/).filter(Boolean)

    if (words.length > 1) {
      return `${words[0][0]}${words[1][0]}`
    }

    return words[0]?.slice(0, 2) || ''
  }

  public sortByChanged(){
    this.sortByLocationZipcode = ""
    this.filter.sortByOpts = new SearchFilterSortByOpts()
  }

  public sortByLocationZipcodeChanged(){
    if(this.sortByLocationZipcode.length > 0 && this.sortByLocationZipcode.length < 5){
      return; //wait until at least 5 characters are provided.
    }
    if(this.sortByLocationZipcode && this.sortByLocationZipcode.trim() != ''){
      this.filter.sortByOpts.locationZipcodes = [this.sortByLocationZipcode.trim()]
    } else {
      this.filter.sortByOpts = new SearchFilterSortByOpts()
    }
    this.querySources(true)
  }

  public get isSortByLocationZipcodeActive(): boolean {
    return !!(this.filter?.sortByOpts?.locationZipcodes?.length);
  }
  protected readonly navigator = navigator;
}
